import {
  FLAMING_ORDER_BOOK_TRADING_PAIRS,
  FLAMINGO_ORDER_BOOK_TOKENS,
} from '@/libs/flamingo-orderbook/constants'
import {
  TFlamingoOrderBookToken,
  TFlamingoOrderBookTradingPair,
} from '@/libs/flamingo-orderbook/types'
import {NeonInvoker} from '@cityofzion/neon-invoker'
import {tx, wallet} from '@cityofzion/neon-core'
import {RpcHelper} from '@/helpers/RpcHelper'

export class FlamingoOrderBook {
  static neonInvoker: NeonInvoker | null = null
  private static neonInvokerRpc: string | null = null

  static async getNeonInvoker(): Promise<NeonInvoker> {
    const rpcAddress = await RpcHelper.getActiveRpc()

    if (!this.neonInvoker || this.neonInvokerRpc !== rpcAddress) {
      this.neonInvoker = await NeonInvoker.init({
        rpcAddress,
        account: new wallet.Account(),
      })
      this.neonInvokerRpc = rpcAddress
    }

    return this.neonInvoker
  }

  static getTradingRoute(tokens: string[]) {
    const [fromToken, toToken] = tokens

    const graph = new Map<string, string[]>()
    const pairMap = new Map<string, TFlamingoOrderBookTradingPair>()

    for (const pair of FLAMING_ORDER_BOOK_TRADING_PAIRS) {
      if (
        !pair ||
        typeof pair.ticker !== 'string' ||
        !pair.ticker.includes('/')
      ) {
        continue
      }

      const [tokenA, tokenB] = pair.ticker.split('/')

      if (!graph.has(tokenA)) graph.set(tokenA, [])
      if (!graph.has(tokenB)) graph.set(tokenB, [])

      graph.get(tokenA)!.push(tokenB)
      graph.get(tokenB)!.push(tokenA)

      pairMap.set(`${tokenA}_${tokenB}`, pair)
      pairMap.set(`${tokenB}_${tokenA}`, pair)
    }

    const visited = new Set<string>()
    const queue: [string, string[]][] = [[fromToken, [fromToken]]]

    while (queue.length > 0) {
      const [currentToken, path] = queue.shift()!

      if (currentToken === toToken) {
        const pairs: TFlamingoOrderBookTradingPair[] = []

        for (let i = 0; i < path.length - 1; i++) {
          const a = path[i]
          const b = path[i + 1]
          const pair = pairMap.get(`${a}_${b}`)

          if (pair) {
            pairs.push(pair)
          } else {
            throw new Error(`No trading pair found for ${a} -> ${b}`)
          }
        }

        return {path, pairs}
      }

      if (visited.has(currentToken)) continue
      visited.add(currentToken)

      const neighbors = graph.get(currentToken) || []
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          queue.push([neighbor, [...path, neighbor]])
        }
      }
    }

    throw new Error(`No trading route found from ${fromToken} to ${toToken}`)
  }

  static listSwappableTokens(): string[] {
    const swappableTokens = new Set<string>()

    for (const pair of FLAMING_ORDER_BOOK_TRADING_PAIRS) {
      if (!pair?.ticker?.includes('/')) continue

      const [tokenA, tokenB] = pair.ticker.split('/')
      swappableTokens.add(tokenA)
      swappableTokens.add(tokenB)
    }

    return Array.from(swappableTokens)
  }

  static getFlamingoToken(symbol: string): TFlamingoOrderBookToken {
    const tokens = FLAMINGO_ORDER_BOOK_TOKENS
    const token = tokens[symbol]
    if (!token) {
      throw new Error('Token not found in Flamingo Order Book list')
    }

    return token
  }

  static async calculateAmountToReceive(
    tokenToUse: TFlamingoOrderBookToken,
    path: TFlamingoOrderBookToken[],
    pairIds: number[],
    amountToUse: string
  ) {
    const amountToUseFormatted = Number(amountToUse) * 10 ** tokenToUse.decimals

    const neonInvoker = await this.getNeonInvoker()

    const response = await neonInvoker.testInvoke({
      invocations: [
        {
          scriptHash: '0xf40f694362957d56801a8cef7e62a83f7f1b7b0f',
          operation: 'emulateStandardConvert',
          args: [
            {
              type: 'Integer',
              value: amountToUseFormatted,
            },
            {
              type: 'Integer',
              value: 0,
            },
            {
              type: 'Array',
              value: path.map(flamingoToken => {
                return {
                  type: 'Hash160',
                  value: flamingoToken.hash,
                }
              }),
            },
            {
              type: 'Array',
              value: pairIds.map(id => {
                return {
                  type: 'Integer',
                  value: id,
                }
              }),
            },
          ],
        },
      ],
      signers: [
        {
          scopes: tx.WitnessScope.CalledByEntry,
        },
      ],
    })

    const value = response.stack[0].value

    if (!value) return

    return Number(value) / 10 ** path[path.length - 1].decimals
  }
}
